import { useDistanceFormatter } from 'i18n/unitFormat';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { ReactComponent as SvgIntermediate } from 'public/assets/icons/intermediate-runs.svg';
import { ReactComponent as SvgBeginners } from 'public/assets/icons/beginner-runs.svg';
import { ReactComponent as SvgExpert } from 'public/assets/icons/expert-runs.svg';
import { ReactComponent as SvgAdvanced } from 'public/assets/icons/advanced-runs.svg';
import { ReactComponent as SvgRuns } from 'public/assets/icons/runs.svg';
import { ReactComponent as SvgSnow } from 'public/assets/icons/snowflake.svg';
import { ReactComponent as SvgSkiiing } from 'public/assets/icons/skiiing.svg';
import { ReactComponent as SvgTerrainParks } from 'public/assets/icons/terrain-parks.svg';
import { ReactComponent as SvgNightSkiiing } from 'public/assets/icons/night-skiiing.svg';
import { ReactComponent as SvgNightManSkiing } from 'public/assets/icons/man-night-skiiing.svg';
import { ReactComponent as SvgSkiableTerrain } from 'public/assets/icons/skiable-terrain.svg';
import { IResortPathInfo, IResortTerrainInfo } from 'api/resort/types';
import { formatTerrainPercent } from 'util/formatPercent';
import { ISlopes } from 'i18n/types';
import { slopeType } from 'i18n/preferences';
import { locale } from 'i18n/constants';
import { ReactComponent as SvgCircle } from '../../../../public/assets/icons/circle.svg';
import { MetricListItem } from '../MetricListItem';
import styles from './styles.module.scss';

export const TerrainOverview = ({
  data, resortCountry,
}:{
  isFresh: boolean, data: IResortTerrainInfo, pathInfo: IResortPathInfo, resortCountry?: string,
}) => {
  const { t } = useOTSTranslation('common');
  const formatMlKm = useDistanceFormatter('kmOrMile');
  const formatAcHa = useDistanceFormatter('acOrHa');
  const isDACHRegion = !!(resortCountry) && ['DE', 'CH-CH', 'AT-AT'].includes(resortCountry);

  const slope: ISlopes = slopeType(resortCountry);

  const skiableTerrainFormatted = formatAcHa(data.runs.skiableAc);
  const [skiableTerrain, unit] = skiableTerrainFormatted.split(' ');

  return (
    <article id="terrain_overview_widget">
      <h3 className={styles.heading}>{t('resort.skiReport.terrain')}</h3>
      <div>
        {
            slope
            && resortCountry && (resortCountry === 'USA' || resortCountry === 'CAN')
            && (
              <>
                {
                  !!(data.runs.beginner?.total) && !isDACHRegion
                  && (
                    <MetricListItem
                      key={t('resort.overview.beginnersRuns')}
                      title={t('resort.overview.beginnersRuns')}
                      Icon={SvgBeginners}
                      metric={formatTerrainPercent(data.runs.beginner)}
                      className={slope.slopesColors && slope.slopesColors.beginner}
                    />
                  )
                }
                {
                  !!(data.runs.intermediate?.total)
                  && (
                    <MetricListItem
                      key={t('resort.overview.intermediateRuns')}
                      title={t('resort.overview.intermediateRuns')}
                      Icon={SvgIntermediate}
                      metric={formatTerrainPercent(data.runs.intermediate)}
                      className={slope.slopesColors && slope.slopesColors.intermediate}
                    />
                  )
                }
                {
                  !!(data.runs.advanced?.total)
                  && (
                    <MetricListItem
                      key={t('resort.overview.advancedRuns')}
                      title={t('resort.overview.advancedRuns')}
                      Icon={SvgAdvanced}
                      metric={formatTerrainPercent(data.runs.advanced)}
                      className={slope.slopesColors && slope.slopesColors.advanced}
                    />
                  )
                }
                {
                  !!(data.runs.expert?.total)
                  && (
                    <MetricListItem
                      key={t('resort.overview.expertRuns')}
                      title={t('resort.overview.expertRuns')}
                      Icon={SvgExpert}
                      metric={formatTerrainPercent(data.runs.expert)}
                      className={slope.slopesColors && slope.slopesColors.expert}
                    />
                  )
                }
              </>
            )
          }
        {
            slope
            && resortCountry && !(resortCountry === 'USA' || resortCountry === 'CAN')
            && (
              <>
                {
                  !!(data.runs.beginner?.total) && !isDACHRegion
                  && (
                    <MetricListItem
                      key={t('resort.overview.beginnersRuns')}
                      title={t('resort.overview.beginnersRuns')}
                      Icon={SvgCircle}
                      metric={formatTerrainPercent(data.runs.beginner)}
                      className={slope.slopesColors && slope.slopesColors.beginner}
                    />
                  )
                }
                {
                  !!(data.runs.intermediate?.total)
                  && (
                    <MetricListItem
                      key={t('resort.overview.intermediateRuns')}
                      title={t('resort.overview.intermediateRuns')}
                      Icon={SvgCircle}
                      metric={formatTerrainPercent(data.runs.intermediate)}
                      className={slope.slopesColors && slope.slopesColors.intermediate}
                    />
                  )
                }
                {
                  !!(data.runs.advanced?.total)
                  && (
                    <MetricListItem
                      key={t('resort.overview.advancedRuns')}
                      title={t('resort.overview.advancedRuns')}
                      Icon={SvgCircle}
                      metric={formatTerrainPercent(data.runs.advanced)}
                      className={slope.slopesColors && slope.slopesColors.advanced}
                    />
                  )
                }
                {
                  !!(data.runs.expert?.total)
                  && (
                    <MetricListItem
                      key={t('resort.overview.expertRuns')}
                      title={t('resort.overview.expertRuns')}
                      Icon={SvgCircle}
                      metric={formatTerrainPercent(data.runs.expert)}
                      className={slope.slopesColors && slope.slopesColors.expert}
                    />
                  )
                }
              </>
            )
          }
        {
            !!(data.runs?.total)
            && (
              <MetricListItem
                key={t('resort.overview.runsInTotal')}
                title={t('resort.overview.runsInTotal')}
                Icon={SvgRuns}
                metric={data.runs?.total.toFixed(0)}
              />
            )
          }
        {
            !!(data.terrain.parks?.total ?? 0)
            && (
              <MetricListItem
                key={t('resort.overview.terrainParks')}
                title={t('resort.overview.terrainParks')}
                Icon={SvgTerrainParks}
                metric={`${data.terrain.parks?.total ?? 0}`}
              />

            )
          }
        {
            !!(data.runs.longestRunLength)
            && (
              <MetricListItem
                key={t('resort.overview.longestRun')}
                title={t('resort.overview.longestRun')}
                Icon={SvgSkiiing}
                metric={formatMlKm(data.runs.longestRunLength)}
              />
            )
          }
        {
            (
              slope
              && !!(data.runs.skiableAc)
              && resortCountry && (resortCountry === 'USA' || resortCountry === 'CAN')
              && skiableTerrain && unit && Number(skiableTerrain)
            )
            && (
              <MetricListItem
                key={t('resort.overview.skiableTerrain')}
                title={t('resort.overview.skiableTerrain')}
                Icon={SvgSkiableTerrain}
                metric={`${Number(skiableTerrain).toLocaleString([locale, 'en-US'])} ${unit}`}
              />
            )
          }
        {
            (
              slope
              && !!(data.runs.skiableKm)
              && resortCountry && !(resortCountry === 'USA' || resortCountry === 'CAN')
            )
            && (
              <MetricListItem
                key={t('resort.overview.skiableTerrain')}
                title={t('resort.overview.skiableTerrain')}
                Icon={SvgSkiableTerrain}
                metric={formatMlKm(data.runs.skiableKm)}
              />
            )
          }
        {
            !!(data.runs.skiableNightAc)
            && (
              <MetricListItem
                key={t('resort.overview.nightSkiing')}
                title={t('resort.overview.nightSkiing')}
                Icon={SvgNightManSkiing}
                metric={`${formatAcHa(data.runs.skiableNightAc)}`}
              />
            )
          }
        {
            !!(data.runs.skiableNightKm)
            && (
              <MetricListItem
                key={t('resort.overview.nightSkiing')}
                title={t('resort.overview.nightSkiing')}
                Icon={SvgNightSkiiing}
                metric={formatMlKm(data.runs.skiableNightKm)}
              />
            )
          }
        {
            !!(data.runs.snowMakingAc)
            && (
              <MetricListItem
                key={t('resort.overview.snowMaking')}
                title={t('resort.overview.snowMaking')}
                Icon={SvgSnow}
                metric={formatAcHa(data.runs.snowMakingAc)}
              />
            )
          }
        {
            !!(data.runs.snowMakingKm)
            && (
              <MetricListItem
                key={t('resort.overview.snowMaking')}
                title={t('resort.overview.snowMaking')}
                Icon={SvgSnow}
                metric={formatMlKm(data.runs.snowMakingKm)}
              />
            )
          }
      </div>
    </article>
  );
};
